import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileDown, Mail, RefreshCw, Search } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { RewardInvoice } from '@/types/giftCardTypes';

interface RewardInvoicesProps {
  adminId: string;
}

const RewardInvoices: React.FC<RewardInvoicesProps> = ({ adminId }) => {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<RewardInvoice[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  
  useEffect(() => {
    if (!adminId) return;
    fetchInvoices();
  }, [adminId]);

  const fetchInvoices = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { 
          action: 'getRewardInvoices',
          adminId
        }
      });

      if (error) throw new Error(error.message);
      
      // Add mock order numbers for demonstration
      if (data?.invoices) {
        const invoicesWithOrderNumbers = data.invoices.map(invoice => ({
          ...invoice,
          requestOrderNumber: `RO${Math.random().toString().substring(2, 11)}`,
          fulfillmentOrderNumber: `RSO${Math.random().toString().substring(2, 11)}`
        }));
        setInvoices(invoicesWithOrderNumbers);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast({
        title: "Error",
        description: "Failed to load reward invoices",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async (invoiceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { 
          action: 'resendInvoiceEmail',
          invoiceId
        }
      });

      if (error) throw new Error(error.message);
      
      toast({
        title: "Email Sent",
        description: "Invoice email has been resent successfully"
      });
    } catch (error) {
      console.error('Error resending invoice email:', error);
      toast({
        title: "Error",
        description: "Failed to resend invoice email",
        variant: "destructive"
      });
    }
  };

  const handleGeneratePdf = async (invoiceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('tremendous', {
        body: { 
          action: 'generateInvoicePdf',
          invoiceId
        }
      });

      if (error) throw new Error(error.message);
      
      // In a real app, we would download the PDF directly
      toast({
        title: "PDF Generated",
        description: "Invoice PDF has been generated successfully"
      });
    } catch (error) {
      console.error('Error generating invoice PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate invoice PDF",
        variant: "destructive"
      });
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (activeFilter === 'all') return true;
    return invoice.status === activeFilter;
  });

  const StatusBadge = ({ status }: { status: string }) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-500">Paid</Badge>;
      case 'pending':
        return <Badge variant="outline" className="text-amber-500 border-amber-500">Pending</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-xl">Reward Invoices</CardTitle>
            <CardDescription>
              View and manage your reward purchase history
            </CardDescription>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchInvoices}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="px-6 mb-4">
          <Tabs value={activeFilter} onValueChange={setActiveFilter} className="w-full">
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="paid">Paid</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="failed">Failed</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="border-t">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Search className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-muted-foreground">No invoices found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Request #</TableHead>
                    <TableHead>Fulfillment #</TableHead>
                    <TableHead>Card</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        {new Date(invoice.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-blue-600">
                        {invoice.requestOrderNumber}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-green-600">
                        {invoice.fulfillmentOrderNumber}
                      </TableCell>
                      <TableCell>{invoice.cardName || 'Gift Card'}</TableCell>
                      <TableCell>
                        {invoice.recipientEmail ? (
                          <div className="font-mono text-xs">
                            {invoice.recipientName ? `${invoice.recipientName}` : ''}
                            <div>{invoice.recipientEmail}</div>
                          </div>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>${invoice.amount.toFixed(2)}</TableCell>
                      <TableCell>
                        <StatusBadge status={invoice.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleGeneratePdf(invoice.id)}
                          >
                            <FileDown className="h-4 w-4" />
                          </Button>
                          {invoice.recipientEmail && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResendEmail(invoice.id)}
                              disabled={invoice.status !== 'paid'}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RewardInvoices;
